import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DataTable, TableColumn } from '../../../../../ui';
import { NameEmailAvatar } from '../../../../../ui/components/Common/NameEmailAvatar';
import { toast } from '../../../../../ui/toast';
import { agenticAssessmentApi, useGetAgenticResultsQuery } from '../../../../../utils/api_service/agenticAssessmentApi';
import { updateJobApplicantStatus } from '../../../../../utils/api_service/JobOpenings';
import { JobOpeningsContext } from '../../../../../utils/contexts/JobOpening';
import { AgenticResultRow } from '../../../../../utils/types/agentic_assessment_types';
import { RECOMMENDATION_CHIP, formatDate, fullName } from '../constants';
import { ReportView } from './ReportView';

interface ResultsPanelProps {
  jobOpeningId: string;
  openSessionId: string | null;
  onOpenReport: (sessionId: string | null) => void;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

// The stages Applicant Analysis moves candidates to, minus Agentic Assessment itself.
const MOVE_TO = [
  { value: 'New', label: 'New' },
  { value: 'Suitable', label: 'Suitable' },
  { value: 'Auto-Assessment', label: 'Auto-Assessment' },
  { value: 'Outstanding', label: 'Outstanding' },
  { value: 'Shortlisted', label: 'Shortlisted' },
  { value: 'Unsuccessful-Pending', label: 'Unsuccessful' },
];

// One-click choices in each row's ⋯ menu.
const QUICK_MOVES = MOVE_TO.filter((o) => o.value === 'Shortlisted' || o.value === 'Unsuccessful-Pending');

interface RowMenu { applicantId: string; top: number; left: number }

const SORT_VALUE: Record<string, (r: AgenticResultRow) => number | string> = {
  candidate: (r) => fullName(r.firstName, r.lastName).toLowerCase(),
  overallFit: (r) => r.overallFit ?? -1,
  completedAt: (r) => r.completedAt ?? 0,
};

export const ResultsPanel: React.FC<ResultsPanelProps> = ({ jobOpeningId, openSessionId, onOpenReport }) => {
  const dispatch = useDispatch();
  const { jobOpeningElements } = useContext(JobOpeningsContext);
  const { data: rows = [], isLoading } = useGetAgenticResultsQuery(jobOpeningId, { refetchOnMountOrArgChange: true });
  // Best fit first, as the other results pages default to their score.
  const [sort, setSort] = useState<SortConfig>({ key: 'overallFit', direction: 'desc' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  // Fixed-positioned so the table's overflow cannot clip it.
  const [rowMenu, setRowMenu] = useState<RowMenu | null>(null);
  const menusRef = useRef<HTMLDivElement>(null);
  const rowMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen && !rowMenu) return;
    const close = (e: Event) => {
      const target = e.target as Node;
      if (menusRef.current?.contains(target) || rowMenuRef.current?.contains(target)) return;
      // A ⋯ button toggles its own menu on click.
      if ((target as Element).closest?.('[data-row-menu-trigger]')) return;
      setMenuOpen(false);
      setRowMenu(null);
    };
    const closeRowMenu = () => setRowMenu(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeRowMenu, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeRowMenu, true);
    };
  }, [menuOpen, rowMenu]);

  const sortedRows = useMemo(() => {
    const value = SORT_VALUE[sort.key];
    if (!value) return rows;
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av < bv) return sort.direction === 'asc' ? -1 : 1;
      if (av > bv) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sort]);

  // Same column flips direction; a new column starts high-to-low, except names (A–Z).
  const handleSortChange = (key: string) => setSort((prev) => ({
    key,
    direction: prev.key === key
      ? (prev.direction === 'desc' ? 'asc' : 'desc')
      : (key === 'candidate' ? 'asc' : 'desc'),
  }));

  const moveOptions = [
    ...MOVE_TO,
    ...(jobOpeningElements?.includes('Psychometric') ? [{ value: 'Psychometric', label: 'Psychometric' }] : []),
    ...(jobOpeningElements?.includes('Wellness') ? [{ value: 'Wellness', label: 'Wellness' }] : []),
  ];

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.applicantId));
  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => { setSelected(new Set()); setMenuOpen(false); };

  const move = async (ids: string[], status: { value: string; label: string }) => {
    setMenuOpen(false);
    setRowMenu(null);
    setMoving(true);
    try {
      const response = await updateJobApplicantStatus(jobOpeningId, {
        jobApplicantIds: ids,
        applicantStatus: status.value,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.success(`Moved ${ids.length} candidate${ids.length === 1 ? '' : 's'} to ${status.label}.`);
      setSelected((prev) => new Set(Array.from(prev).filter((id) => !ids.includes(id))));
      dispatch(agenticAssessmentApi.util.invalidateTags([
        { type: 'AgenticResults', id: jobOpeningId },
        'AgenticApplicants',
      ]));
    } catch {
      toast.error('Could not move the candidates.');
    } finally {
      setMoving(false);
    }
  };

  const columns: TableColumn<AgenticResultRow>[] = [
    {
      key: 'candidate',
      label: 'CANDIDATE',
      width: '24%',
      align: 'left',
      render: (r) => (
        <div className='flex flex-col items-start gap-1'>
          <NameEmailAvatar
              name={fullName(r.firstName, r.lastName)}
              email={r.email || ''}
              avatar={undefined}
              avatarWidth='32px'
              avatarHeight='32px'
              nameFontSize='13px'
              emailFontSize='11px'
          />
          { r.endedEarly && (
            <span className='ml-10 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800'>
              Ended early
            </span>
          ) }
        </div>
      ),
    },
    {
      key: 'overallFit',
      label: 'FIT SCORE',
      width: '10%',
      align: 'left',
      render: (r) => (
        <span className='text-base font-semibold tabular-nums text-slate-900'>
          { r.overallFit != null ? `${r.overallFit}%` : '—' }
        </span>
      ),
    },
    {
      key: 'recommendation',
      label: 'RECOMMENDATION',
      width: '14%',
      align: 'left',
      disableSort: true,
      render: (r) => r.recommendation ? (
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${RECOMMENDATION_CHIP[r.recommendation]}`}>
          { r.recommendation }
        </span>
      ) : <span>—</span>,
    },
    {
      key: 'competencies',
      label: 'COMPETENCIES VS TARGET',
      width: '30%',
      align: 'left',
      disableSort: true,
      render: (r) => (
        <div className='flex min-w-[260px] flex-wrap gap-1 whitespace-normal'>
          { r.competencies.map((c) => (
            <span
                key={c.competencyId}
                title={`${c.name}: L${c.levelEstimate} vs target L${c.targetLevel}`}
                className={`max-w-[220px] truncate rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${
                  c.confidence === 'none' ? 'bg-slate-100 text-slate-400'
                    : c.meetsTarget ? 'bg-emerald-50 text-emerald-700'
                      : r.mustHaveGaps.includes(c.competencyId) ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'
                }`}
            >
              { c.name } { c.levelEstimate }/{ c.targetLevel }
            </span>
          )) }
        </div>
      ),
    },
    {
      key: 'completedAt',
      label: 'COMPLETED',
      width: '11%',
      align: 'left',
      render: (r) => <span className='text-xs text-slate-500'>{ formatDate(r.completedAt) }</span>,
    },
    {
      key: 'applicantStatus',
      label: 'STAGE NOW',
      width: '11%',
      align: 'left',
      disableSort: true,
      render: (r) => <span className='text-xs text-slate-500'>{ r.applicantStatus || '—' }</span>,
    },
    {
      key: 'actions',
      label: '',
      width: '4%',
      align: 'left',
      disableSort: true,
      render: (r) => (
        <button
            type='button'
            data-row-menu-trigger
            aria-label='Move candidate'
            title='Move to…'
            disabled={moving}
            onClick={(e) => {
              e.stopPropagation(); // the row itself opens the report
              const rect = e.currentTarget.getBoundingClientRect();
              setRowMenu((open) => (open?.applicantId === r.applicantId ? null
                : { applicantId: r.applicantId, top: rect.bottom + 4, left: rect.right - 176 }));
            }}
            className='rounded-md px-2 py-1 text-lg leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
        >
          ⋯
        </button>
      ),
    },
  ];

  const rowMenuStage = rowMenu ? rows.find((r) => r.applicantId === rowMenu.applicantId)?.applicantStatus : undefined;

  if (openSessionId) {
    return <ReportView sessionId={openSessionId} onClose={() => onOpenReport(null)} />;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <h3 className='text-base font-semibold text-slate-900'>Results</h3>
        <p className='text-sm text-slate-500'>
          Completed interviews. Fit Score (out of 100) weighs each competency&apos;s level against its target; a must-have
          below target caps the recommendation.
        </p>
      </div>

      { rows.length > 0 && (
        <div
            className={`flex items-center gap-3 rounded-lg border px-4 py-2 ${
              selected.size > 0 ? 'border-emerald-100 bg-emerald-50' : 'border-slate-200 bg-slate-50'
            }`}
        >
          <span className={`text-sm font-semibold ${selected.size > 0 ? 'text-emerald-800' : 'text-slate-500'}`}>
            { selected.size > 0 ? `${selected.size} selected` : 'Tick candidates to move them, or use ⋯ on a row' }
          </span>
          <div className='relative' ref={menusRef}>
            <button
                type='button'
                disabled={moving || selected.size === 0}
                onClick={() => setMenuOpen((o) => !o)}
                className='rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50'
            >
              { moving ? 'Moving…' : 'Move to… ▾' }
            </button>
            { menuOpen && (
              <div className='absolute left-0 z-20 mt-1 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg'>
                { moveOptions.map((o) => (
                  <button
                      key={o.value}
                      type='button'
                      onClick={() => move(Array.from(selected), o)}
                      className='block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-emerald-50'
                  >
                    { o.label }
                  </button>
                )) }
              </div>
            ) }
          </div>
          { selected.size > 0 && (
            <button type='button' onClick={clearSelection} className='ml-auto text-xs text-slate-500 hover:text-slate-700'>
              Clear
            </button>
          ) }
        </div>
      ) }

      { rowMenu && (
        <div
            ref={rowMenuRef}
            style={{ top: rowMenu.top, left: Math.max(8, rowMenu.left) }}
            className='fixed z-30 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg'
        >
          { QUICK_MOVES.map((o) => (
            <button
                key={o.value}
                type='button'
                disabled={rowMenuStage === o.value}
                onClick={() => move([rowMenu.applicantId], o)}
                className='block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-emerald-50 disabled:cursor-default disabled:text-slate-300 disabled:hover:bg-white'
            >
              { o.value === 'Shortlisted' ? 'Shortlist' : 'Unsuccessful' }
              { rowMenuStage === o.value && <span className='ml-1 text-[11px]'>(current)</span> }
            </button>
          )) }
        </div>
      ) }

      { /* A leaked global `td { text-align: center !important }` beats the table's own alignment. */ }
      <div className='[&_td]:!text-left'>
        <DataTable<AgenticResultRow>
            isLoading={isLoading}
            columns={columns}
            data={sortedRows}
            sortConfig={[sort]}
            onSortChange={handleSortChange}
            onClickRow={(r) => r.sessionId && onOpenReport(r.sessionId)}
            rowsPerPage={sortedRows.length || 1}
            currentPage={1}
            totalPages={1}
            totalRecords={sortedRows.length}
            onPageChange={() => {}}
            hidePagination
            hasCheckBox
            onSelectAll={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.applicantId)))}
            onSelectRow={(_i, r) => toggle(r.applicantId)}
            isRowSelected={(_i, r) => selected.has(r.applicantId)}
            isAllSelected={allSelected}
            isIndeterminate={selected.size > 0 && !allSelected}
        />
      </div>
    </div>
  );
};
